import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, Text, View } from 'react-native';

import { VolunteerEvent } from '@/api/models/volunteerModels';
import { VolunteerEventData, volunteerEventResolver } from '@/lib/validations/VolunteersSchema';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import DatePickerInputField from '../inputs/DateInputPickerField';
import GoogleAutoComplete from '../inputs/GoogleAutocomplete';
import ImageDropZonePickerField from '../inputs/ImageDropZonePickerField';
import ImagePickerField from '../inputs/ImagePickerField';
import TextInputField from '../inputs/TextInputField';

export interface VolunteerEventFormProps {
  initialData?: VolunteerEvent;
  isLoading: boolean;
  fileProgress?: number;
  onSubmitTapped: (data: VolunteerEventData) => void;
}

const VolunteerEventForm = ({ initialData, isLoading, fileProgress, onSubmitTapped }: VolunteerEventFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    resetField,
    setError,
    trigger,
    setFocus,
    formState: { isValid, errors, submitCount }
  } = useForm<VolunteerEventData>({
    defaultValues: {
      name: initialData?.name,
      description: initialData?.description,
      address: initialData?.address,
      thumbnail: typeof initialData?.thumbnail === 'string' ? safeJsonParseArray(initialData?.thumbnail) : [],
      startDate: initialData?.startDate?.toString(),
      location: `${initialData?.location.coordinates[0]},${initialData?.location.coordinates[1]}`
    },
    mode: 'onChange',
    resolver: volunteerEventResolver
  });

  const onSubmit: SubmitHandler<VolunteerEventData> = async (data) => {
    onSubmitTapped(data);
  };

  return (
    <View style={style.formContainer}>
      <TextInputField
        name="name"
        control={control}
        label={i18n.t('profile.create_form.name_label')}
        placeholder={i18n.t('profile.create_form.name_placeholder')}
        onSubmitEditing={() => setFocus('description')}
      />

      <TextInputField
        name="description"
        control={control}
        label={i18n.t('profile.create_form.description_label')}
        placeholder={i18n.t('profile.create_form.description_placeholder')}
        multiline
        minHeight={120}
        maxHeight={120}
        onSubmitEditing={() => setFocus('location')}
      />

      <View style={style.autoComplete}>
        <GoogleAutoComplete
          initialValue={initialData?.address}
          type="address"
          label={i18n.t('profile.create_form.address_label')}
          placeholder={i18n.t('profile.create_form.address_placeholder')}
          predictionsScrollEnabled={false}
          error={errors.address}
          onChange={(result) => {
            if (result) {
              setValue('location', `${result.lat},${result.lng}`);
              setValue('address', result.formattedAddress);
              trigger('address');
            } else {
              resetField('location');
              resetField('address');
              setError('address', {
                type: 'required',
                message: i18n.t('profile.volunteer_form.address_required')
              });
            }
          }}
        />
      </View>

      {Platform.OS === 'web' ? (
        <ImageDropZonePickerField
          control={control}
          name="thumbnail"
          progress={fileProgress}
          label={i18n.t('profile.create_form.images_label')}
          showError={!isValid && submitCount > 0}
        />
      ) : (
        <ImagePickerField
          control={control}
          name="thumbnail"
          progress={fileProgress}
          label={i18n.t('profile.create_form.images_label')}
          showError={!isValid && submitCount > 0}
        />
      )}

      <DatePickerInputField control={control} name="startDate" label={i18n.t('profile.create_form.date_label')} />

      {errors.root && <Text style={style.errorText}>{errors.root.message}</Text>}
      <BrandedButton
        loading={isLoading}
        style={style.btnStyle}
        onPress={handleSubmit(onSubmit)}
        disabled={!isValid && submitCount > 0}>
        {i18n.t('mix.submit')}
      </BrandedButton>
    </View>
  );
};

export default VolunteerEventForm;

const style = StyleSheet.create({
  formContainer: {
    flex: 1,
    flexDirection: 'column',
    gap: 28,
    width: '100%',
    height: '100%'
  },

  btnStyle: {
    width: '100%'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  autoComplete: {
    zIndex: 1
  }
});
